import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { CollectionUtils } from "utilities/collection-utils";
import { Icons } from "atoms/constants/icons";
import { Link } from "react-router-dom";
import { t } from "utilities/localization-utils";
import { ToastManager } from "utilities/toast/toast-manager";
import Badge from "atoms/badges/badge";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import React, { useState } from "react";
import useBookmarksAndCollections from "utilities/hooks/domain/my-link/use-bookmarks-and-collections";
import UserBookmarkRecord from "models/view-models/user-bookmark-record";
import UserCollectionRecord from "models/view-models/user-collection-record";
import useUpdateBookmarkCollections from "utilities/hooks/domain/my-link/use-update-bookmark-collections";
import UpdateBookmarkCollectionsModal from "molecules/modals/update-bookmark-collections-modal";

interface MyBookmarkCollectionInfoProps {
    bookmark: UserBookmarkRecord;
    isAllowedToEdit: boolean;
    onCollectionChange?: () => void;
}
const CSS_CLASS_NAME = "c-my-bookmark-collection-info";
const MyBookmarkCollectionInfo = (props: MyBookmarkCollectionInfoProps) => {
    const { bookmark, isAllowedToEdit } = props;
    const [collectionsToUpdate, setCollectionsToUpdate] = useState<
        UserCollectionRecord[]
    >([]);
    const [showCollectionsModal, setShowCollectionsModal] = useState(false);

    const {
        allCollections,
        userCollectionsBookmarks,
        currentCollections,
        refetchAll,
    } = useBookmarksAndCollections(bookmark.id!);

    const handleCollectionBookmarkUpdateSuccess = async () => {
        await refetchAll();
        setShowCollectionsModal(false);
        ToastManager.success(t("updatedItem", { item: t("collections") }));
    };
    const handleCollectionBookmarkUpdateError = () => {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("updating"),
                resource: t("collections"),
            })
        );
    };

    const { updateUserCollectionBookmarks, saving: savingCollections } =
        useUpdateBookmarkCollections(
            userCollectionsBookmarks,
            handleCollectionBookmarkUpdateSuccess,
            handleCollectionBookmarkUpdateError
        );

    const toggleModal = () => {
        setCollectionsToUpdate(currentCollections);
        setShowCollectionsModal(!showCollectionsModal);
    };

    const handleCollectionUpdate = (newCollections: UserCollectionRecord[]) => {
        setCollectionsToUpdate([...newCollections]);
    };

    const handleCollectionsSave = async () => {
        const collectionIds = collectionsToUpdate.map(
            (collection) => collection.id!
        );
        await updateUserCollectionBookmarks(bookmark.id!, collectionIds);
    };

    return (
        <div className={`${CSS_CLASS_NAME}__collections`}>
            {currentCollections?.map((collection) => (
                <Link
                    className={`${CSS_CLASS_NAME}__collectionLink`}
                    to={collection.getRoute()}>
                    <Badge>
                        <Icon type={Icons.Folder} />
                        {collection.name}
                    </Badge>
                </Link>
            ))}
            {isAllowedToEdit && (
                <Button
                    onClick={toggleModal}
                    size={ButtonSizes.Small}
                    style={ButtonStyles.None}
                    cssClassName={`${CSS_CLASS_NAME}__editCollectionsButton`}>
                    <Icon
                        type={
                            CollectionUtils.isEmpty(currentCollections)
                                ? Icons.Plus
                                : Icons.Edit
                        }
                    />
                    <span
                        className={`${CSS_CLASS_NAME}__editCollectionsButtonText`}>
                        {CollectionUtils.isEmpty(currentCollections) &&
                            t("addItemTo", {
                                item: t("bookmark"),
                                container: t("collection"),
                            })}
                    </span>
                </Button>
            )}
            <UpdateBookmarkCollectionsModal
                allCollections={allCollections}
                handleCollectionUpdate={handleCollectionUpdate}
                collectionsToUpdate={collectionsToUpdate}
                onSave={handleCollectionsSave}
                toggleModal={toggleModal}
                showModal={showCollectionsModal}
                isSaving={savingCollections}
            />
        </div>
    );
};

export default MyBookmarkCollectionInfo;
